import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, EditorContext, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { useEffect } from 'react';

import { BlockquoteButton } from '@/components/tiptap-ui/blockquote-button';
import { ClearFormattingButton } from '@/components/tiptap-ui/clear-formatting-button';
import { HeadingDropdownMenu } from '@/components/tiptap-ui/heading-dropdown-menu';
import { LinkPopover } from '@/components/tiptap-ui/link-popover';
import { ListDropdownMenu } from '@/components/tiptap-ui/list-dropdown-menu';
import { MarkButton } from '@/components/tiptap-ui/mark-button';
import { UndoRedoButton } from '@/components/tiptap-ui/undo-redo-button';
import { VideoPopover } from '@/components/tiptap-ui/video-popover';
import { Toolbar, ToolbarGroup, ToolbarSeparator } from '@/components/tiptap-ui-primitive/toolbar';

import { Video } from './extensions/embed';

import '@/components/tiptap-node/blockquote-node/blockquote-node.scss';
import '@/components/tiptap-node/code-block-node/code-block-node.scss';
import '@/components/tiptap-node/heading-node/heading-node.scss';
import '@/components/tiptap-node/list-node/list-node.scss';
import '@/components/tiptap-node/paragraph-node/paragraph-node.scss';

import './TipTapEditorV3.scss';

interface SimpleEditorProps {
	isEditable?: boolean;
	content: string;
	placeholder?: string;
	className?: string;
	id?: string;
	onChange?: (content: string) => void;
}

const SimpleEditor = ({
	isEditable = true,
	content,
	className = '',
	placeholder = '',
	onChange,
	id = 'simple-editor',
}: SimpleEditorProps) => {
	const editor = useEditor(
		{
			immediatelyRender: false,
			extensions: [
				StarterKit.configure({
					link: {
						openOnClick: false,
						linkOnPaste: true,
					},
				}),
				Placeholder.configure({
					showOnlyWhenEditable: true,
					placeholder,
				}),
				Video,
			],
			content,
			editable: isEditable,
			editorProps: {
				attributes: {
					spellcheck: 'false',
					class: 'tiptap-editor-v3',
				},
			},
			onUpdate: ({ editor: e }) => {
				onChange?.(e.getHTML());
			},
		},
		[id]
	);

	useEffect(() => {
		if (editor && editor.getHTML() !== content) {
			editor.commands.setContent(content);
		}
	}, [content, editor]);

	if (!editor) return null;

	return (
		<div className={`tiptap-v3-wrapper ${className}`}>
			<EditorContext.Provider value={{ editor }}>
				<Toolbar data-variant="">
					<ToolbarGroup>
						<UndoRedoButton action="undo" />
						<UndoRedoButton action="redo" />
					</ToolbarGroup>

					<ToolbarSeparator />

					<ToolbarGroup>
						<HeadingDropdownMenu modal={false} levels={[1, 2, 3, 4]} />
						<ListDropdownMenu modal={false} types={['bulletList', 'orderedList']} />
						<BlockquoteButton />
					</ToolbarGroup>

					<ToolbarSeparator />

					<ToolbarGroup>
						<MarkButton type="bold" />
						<MarkButton type="italic" />
						<MarkButton type="strike" />
						<MarkButton type="underline" />
						<LinkPopover />
						<ClearFormattingButton />
					</ToolbarGroup>

					<ToolbarSeparator />

					<ToolbarGroup>
						<VideoPopover />
					</ToolbarGroup>
				</Toolbar>

				<div className="tiptap-v3-content-wrapper">
					<EditorContent editor={editor} role="presentation" className="tiptap-v3-content" />
				</div>
			</EditorContext.Provider>
		</div>
	);
};

export default SimpleEditor;
